'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'
import Link from 'next/link'
import { SafeImage } from '@/components/ui/safe-image'
import { alternateCardImage } from '@/lib/card-image'
import './card-image.css'

type Props = { src: string; alternate?: string | null; alt: string; href?: string; priority?: boolean; fetchPriority?: 'high' | 'low' | 'auto'; width?: number; height?: number; tabIndex?: number; className?: string; fallback?: ReactNode }

/** Shared product frame: a second photo on mouse hover, no image controls. */
export function CardImage(props: Props) {
  const alternate = alternateCardImage(props.src, props.alternate ? [props.src, props.alternate] : [])
  return <CardImageState key={`${props.src}|${alternate}`} {...props} alternate={alternate} />
}

function CardImageState({ src, alternate, alt, href, priority = false, fetchPriority, width = 260, height = 260, tabIndex, className = '', fallback }: Props) {
  const [hovered, setHovered] = useState(false)
  const frame = useRef<HTMLDivElement>(null)
  const [requested, setRequested] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)
  const showing = !!alternate && !failed && loaded && hovered
  useEffect(() => {
    // Hovering the title or CTA belongs to the same card as its photo.
    const card = frame.current?.closest('[data-product-image-card], [data-card="product"], .group') ?? frame.current
    if (!card) return
    const enter = (event: Event) => {
      if ((event as PointerEvent).pointerType !== 'mouse') return
      setHovered(true)
      if (alternate && !failed) setRequested(true)
    }
    const leave = () => setHovered(false)
    card.addEventListener('pointerenter', enter)
    card.addEventListener('pointerleave', leave)
    return () => {
      card.removeEventListener('pointerenter', enter)
      card.removeEventListener('pointerleave', leave)
    }
  }, [alternate, failed])
  const images = !src && fallback ? fallback : <>
    <SafeImage src={src} fallbackContent={fallback} alt={alt} width={width} height={height} priority={priority} fetchPriority={fetchPriority} loading={priority ? undefined : 'lazy'} className="cimg-photo" />
    {alternate && requested && !failed ? <SafeImage src={alternate} alt="" aria-hidden="true" width={width} height={height} loading="lazy" className={`cimg-photo cimg-alternate${showing ? ' cimg-shown' : ''}`} onLoad={() => setLoaded(true)} onError={() => setFailed(true)} /> : null}
  </>
  return <div ref={frame} role="presentation" className={`cimg ${className}`}>
    {/* FRIDGE-346 → FRIDGE-1134: the image link needs BOTH.
        The aria-label (FRIDGE-346) is the accessible name and must never be
        dropped for bytes. It was not enough on its own: a crawler that reads
        only link TEXT still saw an empty anchor, and this one frame is the
        image link of every product card on articles and /filters/, which is
        most of the 24,297 text-less links the audit counted.
        The sr-only node is real DOM text inside the anchor, so the anchor has
        anchor text. It does NOT change what assistive technology announces:
        aria-label takes precedence over contents in the accessible-name
        computation, so the name is byte-identical to before and the adjacent
        title link is not duplicated into a second announcement. The node is
        position:absolute and clipped (app/globals.css .sr-only), so it
        contributes nothing to layout inside .cimg-link. */}
    {href ? <Link href={href} prefetch={false} tabIndex={tabIndex} aria-label={alt} className="cimg-link">{images}<span className="sr-only">{alt}</span></Link> : <div className="cimg-link">{images}</div>}
    {failed ? <span className="sr-only" role="status">Alternate photo unavailable. Primary photo retained.</span> : null}
  </div>
}
